import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  fetchGroupPreviousChat,
  getGroupChatroomInfo as getGroupChatInfo,
  checkEvaluation,
  fetchPreviousChat as fetchPersonalPreviousChat,
  getChatroomInfo as getPersonalChatInfo,
  getPreviousGameChatList,
} from '~/api';
import { useGetDartsQuery } from '~/features/dart';
import { useIntersectionObserver } from '~/hooks';
import {
  MessageResponse,
  GetChatRoomInfoRequest,
  ChatEvaluationRequest,
  LastEvaluatedKey,
  LastEvaluatedGroupChatKey,
  GetGroupChatRoomInfoRequest,
  GetChatRoomInfoMystery,
  GuessingStep,
  GetChatRoomInfoDefault,
  GetChatRoomInfoGroup,
  AvatarInfo,
  AvatarGender,
} from '~/types';
import { CharacterData, GameChatRoomData } from '~/types/missionGameApi';
import { validateEmail } from '~/utils';
import { useCommonChatStore } from '~/store';
import useToast from '~/store/useToast';

interface useGetChatListProps {
  gameId?: number;
  chatroomId?: string;
  email: string;
  socketConnectSuccess: boolean;
  setMessages: React.Dispatch<React.SetStateAction<MessageResponse[]>>;
  chatType: 'personal' | 'group' | 'mission';
  successEnter: boolean;
  initialGameChatRoomInfo?: GameChatRoomData;
  chatMemberCharacters?: CharacterData[];
}

const useGetChatList = ({
  gameId,
  chatroomId,
  email,
  socketConnectSuccess,
  setMessages,
  chatType,
  successEnter,
  initialGameChatRoomInfo,
  chatMemberCharacters,
}: useGetChatListProps) => {
  const { showToast } = useToast();

  const {
    setSaveLastChatEvaluation,
    setActiveEvaluation,
    setChatRoomInfo,
    setIsWaitingChatRoomInfo,
    resetAll,
  } = useCommonChatStore((state) => ({
    setSaveLastChatEvaluation: state.setSaveLastChatEvaluation,
    setActiveEvaluation: state.setActiveEvaluation,
    setChatRoomInfo: state.setChatRoomInfo,
    setIsWaitingChatRoomInfo: state.setIsWaitingChatRoomInfo,
    resetAll: state.resetAll,
  }));

  const [step, setStep] = useState<GuessingStep | null>(null);
  const [title, setTitle] = useState<string>();
  const [getChatroomInfo, setGetChatroomInfo] = useState<boolean>(false);
  const [isChatRoomError, setIsChatRoomError] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    LastEvaluatedKey | LastEvaluatedGroupChatKey
  >(null);
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });
  const [isPaginationPending, setIsPaginationPending] = useState(false);
  const [prevHeight, setPrevHeight] = useState<number | null>(null);
  const chatSectionRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController>();

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => abortControllerRef.current?.abort();
  }, []);

  const { data: dartBalance, isLoading, error } = useGetDartsQuery();

  useEffect(() => {
    if (!chatroomId || !email) return;
    if (!successEnter) return;
    if (!socketConnectSuccess) return;

    const getChatRoomInfoRequest:
      | GetChatRoomInfoRequest
      | GetGroupChatRoomInfoRequest = {
      chatroom_id: chatroomId,
    };

    const fetchChatRoomInfo = async () => {
      try {
        await setChatRoomData();
        await getChatList();

        setIsChatRoomError(false);
        setGetChatroomInfo(true);
        return;
      } catch (error: any) {
        if (error?.message !== 'canceled') {
          console.error(error);

          showToast('error', 'server error');
          setIsChatRoomError(true);
        }
      } finally {
        setIsWaitingChatRoomInfo(false);
      }
    };

    const setChatRoomData = async () => {
      if (chatType === 'mission' && !!initialGameChatRoomInfo) {
        const chatRoomMembers = chatMemberCharacters
          ? chatMemberCharacters?.map((member) => ({
              id: member.character_id,
              name: member.character_name,
              thumbnail_url: member.character_thumbnail_file_path,
              is_blocked: false,
            }))
          : [];

        const avatarInfo: AvatarInfo[] =
          chatMemberCharacters?.map((avatar) => ({
            id: avatar.avatar_id,
            url: avatar.avatar_file_path,
            gender: avatar.gender as AvatarGender,
            idle_motion_path: avatar.idle_motion_path,
          })) ?? [];

        setChatRoomInfo({
          roomType: 'mission',
          chatName: initialGameChatRoomInfo.name,
          participantsNum: chatRoomMembers.length,
          avatarInfo,
          language: initialGameChatRoomInfo.language,
          chatMembers: chatRoomMembers,
          isSafeChatOn: initialGameChatRoomInfo.safe_chat,
          missionGame: {
            game_chat_id: initialGameChatRoomInfo.game_chat_id,
            bgm_file_path: initialGameChatRoomInfo.bgm_file_path,
            background_file_path: initialGameChatRoomInfo.background_file_path,
            avatar_list: initialGameChatRoomInfo.avatar_list,
          },
        });
        setTitle(initialGameChatRoomInfo.name);
        return;
      }

      const response =
        chatType === 'group'
          ? await getGroupChatInfo(getChatRoomInfoRequest)
          : chatType === 'personal'
          ? await getPersonalChatInfo(getChatRoomInfoRequest)
          : undefined;

      if (response?.status !== 200) return;

      const { data } = response as {
        data:
          | GetChatRoomInfoDefault
          | GetChatRoomInfoMystery
          | GetChatRoomInfoGroup;
      };

      if ('accessibility' in data) {
        setChatRoomInfo({
          roomType: data.room_type,
          chatName: data.title,
          participantsNum: data.participants_num,
          accessibility: data.accessibility,
          isOwner: data.is_owner,
          language: data.language,
          chatMembers: data.members,
          avatarInfo: data.avatarInfo,
          isNotificationOn: data.notification_on,
        });
      } else {
        if (data.room_type === 'mystery') {
          const mysteryData = data as GetChatRoomInfoMystery;
          setChatRoomInfo({
            roomType: mysteryData.room_type,
            chatName: mysteryData.title,
            chatMembers: mysteryData.members,
            avatarInfo: mysteryData.avatarInfo,
            language: mysteryData.language,
            isNotificationOn: mysteryData.notification_on,
            assistant_line: mysteryData.assistant_line,
            case_note: mysteryData.case_note,
            background_url: mysteryData.background_url,
            step: mysteryData.step,
          });

          setStep(
            mysteryData.step === null ? 'Introduction' : mysteryData.step,
          );
        } else {
          setChatRoomInfo({
            roomType: data.room_type,
            chatName: data.title,
            chatMembers: data.members,
            avatarInfo: data.avatarInfo,
            language: data.language,
            isNotificationOn: data.notification_on,
            isSafeChatOn: data.safe_chat,
            background_url: data.background_url,
          });
        }
      }
      setTitle(data.title);
    };

    const getChatList = async () => {
      try {
        const response =
          chatType === 'group'
            ? await fetchGroupPreviousChat({
                groupchat_id: chatroomId,
                last_evaluated_key: null,
              })
            : chatType === 'personal'
            ? await fetchPersonalPreviousChat({
                chatroom_id: chatroomId,
                last_evaluated_key: null,
              })
            : chatType === 'mission' && gameId
            ? await getPreviousGameChatList({
                game_id: gameId,
                game_chat_id: Number(chatroomId),
                last_evaluated_key: null,
              })
            : undefined;

        if (response?.status !== 200) {
          showToast('error', 'server error');
          setIsChatRoomError(true);
          return;
        }

        const prevMessages = response.data.map(({ msg, id, send_time }) => {
          return {
            msg,
            id: id ?? '',
            send_time,
          };
        });
        setMessages(prevMessages);

        if (chatType === 'personal' || chatType === 'group') {
          const { id, send_time } = prevMessages[prevMessages.length - 1];
          if (!validateEmail(id)) {
            const requestData: ChatEvaluationRequest = {
              send_time: send_time ? send_time : '',
              chatroom_id: chatroomId,
            };
            const { data } = await checkEvaluation(requestData);
            setSaveLastChatEvaluation({
              id,
              send_time,
              like: data.like,
            });
          }
          setActiveEvaluation({ id, send_time });
        }
        setLastEvaluatedKey(
          response.data_additional?.last_evaluated_key as LastEvaluatedKey,
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchChatRoomInfo();

    return () => {
      resetAll();
    };
  }, [successEnter, socketConnectSuccess, chatroomId, email, chatType]);

  useEffect(() => {
    if (!chatroomId || !email) return;
    if (!isIntersecting || isPaginationPending) return;
    if (!lastEvaluatedKey) return;

    const getChatList = async () => {
      try {
        setIsPaginationPending(true);

        const response =
          chatType === 'group'
            ? await fetchGroupPreviousChat({
                groupchat_id: chatroomId,
                last_evaluated_key:
                  lastEvaluatedKey as LastEvaluatedGroupChatKey,
              })
            : chatType === 'personal'
            ? await fetchPersonalPreviousChat({
                chatroom_id: chatroomId,
                last_evaluated_key: lastEvaluatedKey as LastEvaluatedKey,
              })
            : chatType === 'mission' && gameId
            ? await getPreviousGameChatList({
                game_id: gameId,
                game_chat_id: Number(chatroomId),
                last_evaluated_key: lastEvaluatedKey as LastEvaluatedKey,
              })
            : undefined;

        if (response?.status !== 200) {
          showToast('error', 'server error');
          setIsChatRoomError(true);
          return;
        }

        const prevMessages = response.data.map(({ msg, id, send_time }) => {
          return {
            msg,
            id: id ?? '',
            send_time,
          };
        });
        setMessages((prev) => [...prevMessages, ...prev]);
        setLastEvaluatedKey(
          response.data_additional?.last_evaluated_key as LastEvaluatedKey,
        );
        if (chatSectionRef.current)
          setPrevHeight(chatSectionRef.current.scrollTop);
      } catch (error) {
        console.error(error);
      } finally {
        setIsPaginationPending(false);
      }
    };
    getChatList();
  }, [
    chatroomId,
    email,
    isIntersecting,
    lastEvaluatedKey,
    isPaginationPending,
    chatType,
  ]);

  return {
    step,
    setStep,
    getChatroomInfo,
    isChatRoomError,
    title,
    isPaginationPending,
    infoMessageRef: ref,
    chatSectionRef,
    prevHeight,
  };
};

export default useGetChatList;
